// CH-W39 — Marketing UTM tracking (customer). GA-style: rekam visit saat landing ber-UTM,
// rekam pageview tiap perpindahan route (batch via sendBeacon), simpan VisitId (localStorage)
// untuk dilampirkan ke cart -> atribusi sampai booking.
import { API_URL, MAINCONFIG } from "@/mainconfig";
import store from "@/store";

const VISIT_KEY = "mkt_visit_id";
const SESSION_KEY = "mkt_session_id";
const CAPTURED_KEY = "mkt_captured"; // set param yg sudah di-track pada sesi ini (cegah double-fire)
const TRACKING_PARAMS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "gclid", "fbclid", "ref"];
const FLUSH_AT = 6; // flush bila buffer mencapai N
const FLUSH_IDLE_MS = 2500; // flush otomatis setelah idle (tak perlu tutup tab)

let buffer = [];
let flushTimer = null;

function uuid() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch (e) { /* ignore */ }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function sessionId() {
  let sid = sessionStorage.getItem(SESSION_KEY);
  if (!sid) { sid = uuid(); sessionStorage.setItem(SESSION_KEY, sid); }
  return sid;
}

export function getVisitId() {
  return localStorage.getItem(VISIT_KEY) || null;
}

function fingerprintId() {
  try { return store.state.user?.fingerprintId || localStorage.getItem("fingerprintId") || null; }
  catch (e) { return localStorage.getItem("fingerprintId") || null; }
}

function hasTrackingParam(query) {
  return Object.keys(query || {}).some((k) => TRACKING_PARAMS.includes(k.toLowerCase()) && String(query[k]).trim() !== "");
}

// Rekam 1 visit saat landing ber-UTM. Non-blocking; simpan VisitId ke localStorage (last-touch).
export async function captureVisit(to) {
  try {
    const query = to.query || {};
    if (!hasTrackingParam(query)) return;
    const key = JSON.stringify(query);
    if (sessionStorage.getItem(CAPTURED_KEY) === key) return; // sudah di-track set param ini
    sessionStorage.setItem(CAPTURED_KEY, key);

    const res = await API_URL.post("/marketing/track", {
      QueryParams: query,
      Host: typeof window !== "undefined" ? window.location.hostname : "",
      LandingPath: to.fullPath,
      Referrer: typeof document !== "undefined" ? document.referrer : "",
      FingerprintId: fingerprintId(),
      PropertyId: currentPropertyId(),
    });
    const visitId = res?.data?.data?.VisitId;
    if (visitId) localStorage.setItem(VISIT_KEY, visitId);
  } catch (e) { /* fire-and-forget: jangan blok navigasi */ }
}

// PropertyId konteks (dari room detail yang termuat di store) — untuk owner-scope report.
function currentPropertyId() {
  try {
    const d = store.getters["properties/roomDetailsData"];
    return d?.data?.PropertyId || d?.PropertyId || null;
  } catch (e) { return null; }
}

// Basic view dari route (dipanggil di afterEach). Entity diperkaya oleh halaman via patchCurrentView.
export function recordView(to) {
  try {
    const view = {
      Path: to.path,
      RouteName: to.name || null,
      EntityType: null,
      EntityId: to.params?.slug || null,
      PropertyId: null,
      DisplayName: null,
      Referrer: typeof document !== "undefined" ? document.referrer : "",
      CreatedOn: new Date().toISOString().slice(0, 19).replace("T", " "),
    };
    buffer.push(view);
    if (buffer.length >= FLUSH_AT) flush();
    else scheduleFlush();
  } catch (e) { /* ignore */ }
}

// flush otomatis setelah idle beberapa detik (tanpa perlu tutup tab / capai 6 halaman)
function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => { flushTimer = null; flush(); }, FLUSH_IDLE_MS);
}

// Tag PropertyId ke visit saat ini (visit di-capture saat landing → PropertyId biasanya null).
// Membuat visit bisa di-scope ke owner (agent) begitu halaman properti termuat. Idempotent di backend (hanya isi bila null).
export function tagVisitProperty(propertyId) {
  try {
    const visitId = getVisitId();
    if (!visitId || !propertyId) return;
    API_URL.post("/marketing/attach", { VisitId: visitId, PropertyId: propertyId });
  } catch (e) { /* ignore */ }
}

// Perkaya view terakhir dengan konteks entity (dipanggil halaman RoomDetail/PropertyPage setelah data termuat).
export function patchCurrentView(fields) {
  try {
    if (!buffer.length) return;
    Object.assign(buffer[buffer.length - 1], fields || {});
  } catch (e) { /* ignore */ }
}

// Kirim batch pageview. Pakai fetch keepalive (CORS benar lintas-origin + tetap terkirim saat tab ditutup).
// Catatan: sendBeacon TIDAK dipakai — Content-Type application/json lintas-origin butuh preflight yang tak bisa dilakukan beacon.
export function flush() {
  if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }
  if (!buffer.length) return;
  const payload = {
    SessionId: sessionId(),
    FingerprintId: fingerprintId(),
    VisitId: getVisitId(),
    Host: typeof window !== "undefined" ? window.location.hostname : "",
    Views: buffer.splice(0, buffer.length),
  };
  const url = `${MAINCONFIG.BASE_URL}/marketing/pageview`;
  try {
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
      credentials: "omit",
    }).catch(() => { /* fire-and-forget */ });
  } catch (e) {
    try { API_URL.post("/marketing/pageview", payload); } catch (e2) { /* ignore */ }
  }
}

// Flush saat tab disembunyikan/ditutup agar pageview terakhir tak hilang.
export function installFlushHooks() {
  if (typeof window === "undefined") return;
  window.addEventListener("visibilitychange", () => { if (document.visibilityState === "hidden") flush(); });
  window.addEventListener("pagehide", flush);
}
